import { type ReactNode, useCallback, useMemo, useState } from 'react';
import type { DirtyStateContextValue } from '../types';
import { DirtyStateContext } from '../contexts';

interface DirtyStateProviderProps {
  children: ReactNode;
}

export const DirtyStateProvider = ({ children }: DirtyStateProviderProps) => {
  const [dirtyStore, setDirtyStore] = useState<Record<string, boolean>>({});

  const markClean = useCallback(
    (id: string) => {
      setDirtyStore(prev => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
    },
    [setDirtyStore]
  );

  const markDirty = useCallback(
    (id: string) => {
      setDirtyStore(prev => ({ ...prev, [id]: true }));
    },
    [setDirtyStore]
  );

  const reset = useCallback(
    (from?: string) => {
      if (from) {
        setDirtyStore(prev => {
          const updated = { ...prev };
          delete updated[from];
          return updated;
        });
      } else {
        setDirtyStore({});
      }
    },
    [setDirtyStore]
  );

  const checkScopeIsDirty = useCallback(
    (id?: string) => {
      if (id) {
        return Boolean(dirtyStore[id]);
      }
      return Object.keys(dirtyStore).length > 0;
    },
    [dirtyStore]
  );

  const contextValue: DirtyStateContextValue = useMemo(
    () => ({
      markClean,
      markDirty,
      clearDirtyState: reset,
      checkScopeIsDirty,
    }),
    [markClean, markDirty, reset, checkScopeIsDirty]
  );

  return (
    <DirtyStateContext.Provider value={contextValue}>
      {children}
    </DirtyStateContext.Provider>
  );
};
